import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Authenticated,
  useCan,
  useNavigation,
  useParsed,
} from "@refinedev/core";
import {
  Card,
  Flex,
  Form,
  FormInstance,
  FormProps,
  Spin,
  ConfigProvider,
  Col,
  Row,
  Divider,
  Typography,
} from "antd";
import { BrandKit } from "../types";
import { BrandKitAsset, PaletteResponse } from "pages/media/types";
import { StagedBrandKitAsset } from "types";
import { useAntTheme } from "hooks/useAntTheme";
import { UploadRefs, useBrandKitEdit } from "./hooks/useBrandkitEdit";
import { PageBreadcrumbsHeader } from "pages/components/PageBreadcrumbsHeader/";
import styled from "styled-components";
import { pageBleedCss } from "components/layout/pagePadding";
import { useTranslate } from "#src/hooks";
import { FontsCard } from "./components/preview/FontsCard";
import { ColorsCard } from "./components/preview/ColorsCard";
import { BrandKitAssets } from "./components/BrandKitAssets";
import { BrandKitPreview } from "./components/preview/BrandKitPreview";
import { ActionButton } from "pages/components/ActionButton/";
import { CheckIcon } from "@phosphor-icons/react";
import { useSession } from "hooks/useSession";
import { radius } from "@libertify/ui";

export const BrandKitEdit: React.FC = () => {
  const params = useParams();
  const { params: parsedParams } = useParsed();
  const cameFromCreate = parsedParams?.from === "create";
  const brandKitId = params.id;
  const canEdit = useCan({ resource: "media_brandkits", action: "edit" });
  const { list } = useNavigation();
  const t = useTranslate();

  const {
    saveButtonProps,
    form,
    formProps,
    fontRefs,
    videoRefs,
    imageRefs,
    musicRefs,
    brandKit,
    saving,
    optimisticRemoveAsset,
    handleEditSave,
    stagedAssets,
    setStagedAssets,
    isBrandKitLoading,
    setDefaultFonts,
    handleFontSelect,
    fonts,
    updateFontStateOptimistically,
  } = useBrandKitEdit(brandKitId);

  const refs: UploadRefs = {
    music: musicRefs,
    font: fontRefs,
    video: videoRefs,
    image: imageRefs,
  };

  return (
    <Authenticated key="brandkit_edit">
      <StyledWrapper>
        <PageBreadcrumbsHeader
          title={t("media.brandkit.edit.editBrandKit")}
          subtitle={t("media.brandkit.edit.brandKitEditDescription")}
          style={{ margin: 0 }}
        />
        <MainCard>
          <BaseBrandKitEdit
            brandKit={brandKit}
            loading={isBrandKitLoading}
            onEditSave={(values) =>
              handleEditSave(
                values,
                cameFromCreate ? () => list("media_brandkits") : undefined
              )
            }
            stagedAssets={stagedAssets}
            setStagedAssets={setStagedAssets}
            optimisticRemoveAsset={optimisticRemoveAsset}
            form={form}
            formProps={formProps}
            uploadRefs={refs}
            setDefaultFonts={setDefaultFonts}
            handleFontSelect={handleFontSelect}
            fonts={fonts}
            updateFontStateOptimistically={updateFontStateOptimistically}
          />
        </MainCard>
        <Flex gap={16} justify="center" style={{ paddingBlockEnd: "24px" }}>
          <ActionButton
            onClick={() => list("media_brandkits")}
            iconPosition="start"
            disabled={saving}
            style={{
              maxWidth: "50%",
              height: 50,
            }}
          >
            {t("buttons.back")}
          </ActionButton>
          <ActionButton
            {...saveButtonProps}
            type="primary"
            size="small"
            loading={saving}
            disabled={!canEdit.data || saving}
            style={{
              maxWidth: "50%",
              height: 50,
            }}
            icon={<CheckIcon />}
          >
            {t(
              `media.brandkit.edit.${cameFromCreate ? "createBrandKit" : "saveBrandKit"}`
            )}
          </ActionButton>
        </Flex>
      </StyledWrapper>
    </Authenticated>
  );
};

export const StyledWrapper = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Cancel the app shell's page padding (full-bleed editor) */
  ${({ theme }) => pageBleedCss(theme)}

  .ant-card {
    border-radius: ${radius.box}px;
  }
  .ant-page-header-content {
    padding-block-start: 16px;
  }

  .ant-card-actions {
    background: none;
    border: 0;
    > li > span > .ant-space {
      display: flex;
      float: none !important;
      width: 100%;
      > .ant-space-item {
        width: 100%;
      }
    }
  }
`;

const MainCard = styled(Card)`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  margin: 24px;
  display: flex;
  flex-direction: column;

  > .ant-card-head {
    flex-shrink: 0;
  }

  > .ant-card-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 32px;
    display: flex;
    flex-direction: column;
  }
`;

const ScrollableSettingsColumn = styled.div`
  height: 100%;
  overflow-y: auto;
  padding-inline-end: 24px;
  margin-inline-end: 8px;
`;

type BaseBrandKitEditProps = {
  brandKit?: BrandKit;
  loading?: boolean;
  onEditSave: (values: {
    name: string;
    colors: PaletteResponse;
  }) => Promise<void>;
  onSuccess?: () => void;
  stagedAssets: StagedBrandKitAsset[];
  setStagedAssets: React.Dispatch<React.SetStateAction<StagedBrandKitAsset[]>>;
  uploadRefs: UploadRefs;
  optimisticRemoveAsset: (assetId: string) => void;
  form: FormInstance;
  formProps: FormProps;
  setDefaultFonts: (removedAssetId?: string) => Promise<void>;
  handleFontSelect: (
    purpose: "Title" | "Text" | "Subtitles",
    fontId: string
  ) => void;
  fonts: BrandKitAsset[];
  updateFontStateOptimistically: (
    purpose: string,
    update: { asset_id: string; description?: string; path?: string } | null
  ) => void;
};

type Organization = NonNullable<
  ReturnType<typeof useSession>["currentOrganization"]
>;

export const BaseBrandKitEdit = ({
  brandKit,
  loading = false,
  ...rest
}: BaseBrandKitEditProps) => {
  const { currentOrganization: organization } = useSession();

  if (loading || !brandKit || !organization) {
    return <Spin size="large" fullscreen />;
  }

  return (
    <LoadedBrandKitEdit
      {...rest}
      brandKit={brandKit}
      organization={organization}
    />
  );
};

const LoadedBrandKitEdit = ({
  brandKit,
  organization,
  onEditSave,
  onSuccess,
  stagedAssets,
  setStagedAssets,
  optimisticRemoveAsset,
  formProps,
  form,
  uploadRefs,
  setDefaultFonts,
  handleFontSelect,
  fonts,
  updateFontStateOptimistically,
}: Omit<BaseBrandKitEditProps, "brandKit" | "loading"> & {
  brandKit: BrandKit;
  organization: Organization;
}) => {
  const { theme } = useAntTheme();
  const [colors, setColors] = useState<Partial<PaletteResponse>>({});
  const [brandKitName, setBrandKitName] = useState(brandKit.name);

  const handleFinish = async () => {
    await onEditSave({
      name: brandKitName,
      colors: { ...brandKit.palette, ...colors },
    });
    onSuccess?.();
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Tabs: {
            itemColor: theme.colorTextDescription,
            verticalItemPadding: "8px",
          },
          Typography: {
            fontFamily: "Inter Variable",
          },
        },
      }}
    >
      <Typography.Title
        level={2}
        style={{ margin: 0, fontWeight: 500 }}
        editable={{
          enterIcon: null,
          triggerType: ["text", "icon"],
          tooltip: false,
          onChange: (value) => setBrandKitName(value || brandKitName),
        }}
      >
        {brandKitName}
      </Typography.Title>
      <Divider style={{ marginBottom: 0 }} />
      <Form
        requiredMark
        layout={"vertical"}
        {...formProps}
        form={form}
        onFinish={() => {
          handleFinish().catch((error) =>
            console.error("Error saving brandkit", error)
          );
        }}
        style={{ width: "100%", flex: 1, minHeight: 0 }}
      >
        <Row style={{ height: "100%", margin: 0 }}>
          <Col sm={24} md={16} style={{ height: "100%" }}>
            <ScrollableSettingsColumn>
              <Flex vertical gap={8} style={{ width: "100%", paddingTop: 16 }}>
                <BrandKitAssets
                  brandKitAssets={brandKit.assets}
                  uploadRefs={uploadRefs}
                  organizationId={organization.id}
                  stagedAssets={stagedAssets}
                  setStagedAssets={setStagedAssets}
                  onAssetDeleteSuccess={optimisticRemoveAsset}
                  onFontDeleted={setDefaultFonts}
                  updateFontStateOptimistically={updateFontStateOptimistically}
                  brandKitId={brandKit.id}
                />
                <Divider />
                <ColorsCard
                  form={form}
                  setColors={setColors}
                  palette={brandKit.palette}
                />
                <Divider />
                <FontsCard
                  fonts={fonts}
                  setDefaultFonts={setDefaultFonts}
                  handleFontSelect={handleFontSelect}
                  updateFontStateOptimistically={updateFontStateOptimistically}
                />
              </Flex>
            </ScrollableSettingsColumn>
          </Col>
          <Col sm={0} md={8} style={{ height: "100%" }}>
            <BrandKitPreview
              pickedColors={{ ...brandKit.palette, ...colors }}
              fonts={fonts}
              brandKitAssets={brandKit.assets}
              stagedAssets={stagedAssets}
            />
          </Col>
        </Row>
      </Form>
    </ConfigProvider>
  );
};
